import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import {
  Sparkles, AlertTriangle, FileText, Pill, ShieldCheck, Calendar,
  ClipboardList, TrendingUp, CheckCircle2, ArrowRight, Activity,
  Brain, Bell, Heart, Eye, Stethoscope, Send, Star, ChevronDown, ChevronUp,
  MoreHorizontal, Clock as ClockIcon, Droplets, Wind
} from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAppMode } from '../../contexts/AppModeContext';

// ── Cohort Color System ──
const COHORT_CONFIG = {
  diabetes: { label: 'Diabetes', color: '#0ea5e9', icon: Droplets },
  cardiovascular: { label: 'Cardiovascular', color: '#ef4444', icon: Heart },
  preventive: { label: 'Preventive Care', color: '#22c55e', icon: ShieldCheck },
  behavioral: { label: 'Behavioral Health', color: '#8b5cf6', icon: Brain },
  respiratory: { label: 'Respiratory', color: '#f59e0b', icon: Wind },
  medication: { label: 'Medication', color: '#6366f1', icon: Pill },
  mips: { label: 'MIPS / Quality', color: '#6b5ce7', icon: Star },
  hcc: { label: 'HCC / Risk', color: '#d97706', icon: TrendingUp },
};

// ── Patient Population Data ──
const COHORT_PATIENTS = {
  diabetes: {
    patientCount: 42,
    urgentGap: '8 HbA1c overdue',
    addressedRate: 72,
    alerts: [
      { id: 'D1', severity: 'high', patient: 'John Smith', patientId: 'P001', desc: 'HbA1c overdue 9 months. Last A1c 7.2% (above target). Therapy intensification recommended.', action: 'Order Lab', actionType: 'order' },
      { id: 'D2', severity: 'high', patient: 'Carlos Mendez', patientId: 'F101', desc: 'HbA1c overdue 6 months. On Metformin 1000mg BID. Consider A1c recheck.', action: 'Order Lab', actionType: 'order' },
      { id: 'D3', severity: 'medium', patient: 'John Smith', patientId: 'P001', desc: 'Diabetic eye exam overdue by 2 months. Last exam > 14 months ago.', action: 'Refer', actionType: 'referral' },
      { id: 'D4', severity: 'medium', patient: 'John Smith', patientId: 'P001', desc: 'Diabetic foot exam overdue. Annual screening recommended per ADA.', action: 'Order Exam', actionType: 'order' },
      { id: 'D5', severity: 'medium', patient: 'John Smith', patientId: 'P001', desc: 'Urine albumin/creatinine ratio not checked in 12+ months. Screen for nephropathy.', action: 'Order Lab', actionType: 'order' },
      { id: 'D6', severity: 'low', patient: 'Susan Clark', patientId: 'F104', desc: 'Diabetes self-management education not documented this year.', action: 'Schedule', actionType: 'schedule' },
    ],
  },
  cardiovascular: {
    patientCount: 38,
    urgentGap: '5 BP above target',
    addressedRate: 81,
    alerts: [
      { id: 'CV1', severity: 'high', patient: 'John Smith', patientId: 'P001', desc: 'BP 138/88 exceeds 130/80 target. On Lisinopril 20mg. Consider adding amlodipine 5mg.', action: 'Update Chart', actionType: 'chart' },
      { id: 'CV2', severity: 'medium', patient: 'Emily Davis', patientId: 'P004', desc: 'HTN follow-up overdue. BP trend has been borderline last 3 visits.', action: 'Schedule', actionType: 'schedule' },
      { id: 'CV3', severity: 'medium', patient: 'Richard Adams', patientId: 'F203', desc: 'CHF optimization review due. Last echo > 12 months. BNP trending up.', action: 'Order Lab', actionType: 'order' },
      { id: 'CV4', severity: 'low', patient: 'Emily Davis', patientId: 'P004', desc: 'Home BP monitoring recommended. Patient has borderline readings in-office.', action: 'Update Chart', actionType: 'chart' },
    ],
  },
  preventive: {
    patientCount: 312,
    urgentGap: '24 screenings overdue',
    addressedRate: 68,
    alerts: [
      { id: 'PR1', severity: 'medium', patient: 'Robert Johnson', patientId: 'P003', desc: 'Colonoscopy screening overdue. Age 45 — meets USPSTF criteria for screening.', action: 'Schedule', actionType: 'schedule' },
      { id: 'PR2', severity: 'medium', patient: 'Maria Garcia', patientId: 'P002', desc: 'Mammogram overdue. Last screening > 18 months ago.', action: 'Schedule', actionType: 'schedule' },
      { id: 'PR3', severity: 'medium', patient: 'John Smith', patientId: 'P001', desc: 'Annual Wellness Visit not scheduled this year. Medicare eligible.', action: 'Schedule', actionType: 'schedule' },
      { id: 'PR4', severity: 'low', patient: 'Emily Davis', patientId: 'P004', desc: 'Influenza vaccination not documented for this season.', action: 'Update Chart', actionType: 'chart' },
      { id: 'PR5', severity: 'low', patient: 'Sarah Williams', patientId: 'P005', desc: 'PHQ-9 depression screening not documented this year.', action: 'Update Chart', actionType: 'chart' },
    ],
  },
  behavioral: {
    patientCount: 18,
    urgentGap: '6 PHQ-9 overdue',
    addressedRate: 55,
    alerts: [
      { id: 'BH1', severity: 'medium', patient: 'Michael Brown', patientId: 'P007', desc: 'Depression medication check overdue. On SSRI — 6-month follow-up recommended.', action: 'Schedule', actionType: 'schedule' },
      { id: 'BH2', severity: 'low', patient: 'Emily Davis', patientId: 'P004', desc: 'PHQ-9 screening not documented this year. Annual screening recommended.', action: 'Update Chart', actionType: 'chart' },
    ],
  },
  respiratory: {
    patientCount: 12,
    urgentGap: '2 spirometry overdue',
    addressedRate: 75,
    alerts: [
      { id: 'RE1', severity: 'medium', patient: 'James Taylor', patientId: 'P005', desc: 'COPD exacerbation follow-up needed. Spirometry not done in 14 months.', action: 'Schedule', actionType: 'schedule' },
      { id: 'RE2', severity: 'low', patient: 'Kevin Robinson', patientId: 'F201', desc: 'Asthma action plan review due. Last update > 12 months.', action: 'Update Chart', actionType: 'chart' },
    ],
  },
  medication: {
    patientCount: 28,
    urgentGap: '3 interactions flagged',
    addressedRate: 85,
    alerts: [
      { id: 'MED1', severity: 'high', patient: 'Robert Johnson', patientId: 'P003', desc: 'Gabapentin and Pregabalin both active. Possible therapeutic duplication — review and reconcile.', action: 'Review Meds', actionType: 'chart' },
      { id: 'MED2', severity: 'medium', patient: 'John Smith', patientId: 'P001', desc: 'Potential interaction: Metformin + contrast dye order. Hold Metformin 48h peri-procedure.', action: 'Review', actionType: 'chart' },
    ],
  },
  mips: {
    patientCount: 156,
    urgentGap: '12 measure gaps',
    addressedRate: 78,
    alerts: [
      { id: 'MI1', severity: 'high', patient: 'John Smith', patientId: 'P001', desc: 'Diabetes HbA1c Control measure — patient eligible. A1c 7.2% not at target. Impacts MIPS score.', action: 'View Measure', actionType: 'mips' },
      { id: 'MI2', severity: 'medium', patient: 'Emily Davis', patientId: 'P004', desc: 'Controlling High Blood Pressure measure — document BP at target or treatment adjustment.', action: 'Document', actionType: 'chart' },
      { id: 'MI3', severity: 'low', patient: 'Maria Garcia', patientId: 'P002', desc: 'Tobacco screening and cessation intervention not documented this visit.', action: 'Update Chart', actionType: 'chart' },
    ],
  },
  hcc: {
    patientCount: 45,
    urgentGap: '8 coding gaps',
    addressedRate: 62,
    alerts: [
      { id: 'HCC1', severity: 'high', patient: 'Robert Johnson', patientId: 'P003', desc: 'Chronic Kidney Disease may qualify for HCC coding. Confirm CKD stage documentation for RAF impact.', action: 'Confirm HCC', actionType: 'chart' },
      { id: 'HCC2', severity: 'medium', patient: 'John Smith', patientId: 'P001', desc: 'T2DM with recent elevated A1c may qualify for HCC 18 (Diabetes with Complications) — +0.302 RAF.', action: 'Review Coding', actionType: 'chart' },
      { id: 'HCC3', severity: 'low', patient: 'Maria Garcia', patientId: 'P002', desc: 'Visit complexity may support 99215 instead of 99214. Upcoding opportunity.', action: 'Review Code', actionType: 'chart' },
    ],
  },
};

const SEVERITY_BADGE = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-slate-100 text-slate-600' };

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'diabetes', label: 'Diabetes' },
  { key: 'cardiovascular', label: 'Cardiovascular' },
  { key: 'preventive', label: 'Preventive' },
  { key: 'behavioral', label: 'Behavioral' },
  { key: 'respiratory', label: 'Respiratory' },
  { key: 'medication', label: 'Medication' },
  { key: 'mips', label: 'MIPS' },
  { key: 'hcc', label: 'HCC / Risk' },
];

const LuminaAIInsights = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();
  const [activeCohort, setActiveCohort] = useState('all');
  const [expandedSections, setExpandedSections] = useState({});
  const [snoozed, setSnoozed] = useState([]);
  const [resolved, setResolved] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [sortBy, setSortBy] = useState('urgency');
  const [patientFilter, setPatientFilter] = useState('all');

  const totalPatients = 312;
  const totalGaps = useMemo(() => Object.values(COHORT_PATIENTS).reduce((s, c) => s + c.alerts.length, 0), []);
  const visibleAlerts = useMemo(() => {
    const hidden = new Set([...snoozed, ...resolved, ...dismissed]);
    return Object.entries(COHORT_PATIENTS).flatMap(([cohort, data]) =>
      data.alerts.filter(a => !hidden.has(a.id)).map(a => ({ ...a, cohort }))
    );
  }, [snoozed, resolved, dismissed]);

  const filteredAlerts = activeCohort === 'all' ? visibleAlerts : visibleAlerts.filter(a => a.cohort === activeCohort);

  const sortedAlerts = useMemo(() => {
    const severityOrder = { high: 0, medium: 1, low: 2 };
    return [...filteredAlerts].sort((a, b) => {
      if (sortBy === 'urgency') return severityOrder[a.severity] - severityOrder[b.severity];
      if (sortBy === 'patient') return a.patient.localeCompare(b.patient);
      return 0;
    });
  }, [filteredAlerts, sortBy]);

  // Group by cohort for section display
  const groupedByCohort = useMemo(() => {
    const groups = {};
    sortedAlerts.forEach(a => {
      if (!groups[a.cohort]) groups[a.cohort] = [];
      groups[a.cohort].push(a);
    });
    return groups;
  }, [sortedAlerts]);

  const toggleSection = (cohort) => {
    setExpandedSections(prev => ({ ...prev, [cohort]: !prev[cohort] }));
  };

  const handleAction = (alert) => {
    if (alert.actionType === 'chart') navigate(`/lumina/patients/${alert.patientId}`);
    else if (alert.actionType === 'schedule') toast.success(`Scheduling for ${alert.patient}`);
    else if (alert.actionType === 'order') toast.success(`Lab order initiated for ${alert.patient}`);
    else if (alert.actionType === 'referral') toast.success(`Referral initiated for ${alert.patient}`);
    else if (alert.actionType === 'mips') navigate('/lumina/mips');
    else navigate(`/lumina/patients/${alert.patientId}`);
  };

  return (
    <div className="space-y-5" data-testid="ai-insights-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">AI Insights</h1>
          <p className="text-slate-500 mt-1 text-sm">Population overview &middot; {totalPatients} patients &middot; {visibleAlerts.length} open care gaps</p>
        </div>
        <Badge className="bg-violet-100 text-violet-700 gap-1"><Sparkles className="h-3 w-3" /> Echo-Powered</Badge>
      </div>

      {/* ── Cohort Summary Cards (scrollable row) ── */}
      <div className="flex gap-3 overflow-x-auto pb-2" data-testid="cohort-cards">
        {Object.entries(COHORT_CONFIG).map(([key, config]) => {
          const data = COHORT_PATIENTS[key];
          if (!data) return null;
          const Icon = config.icon;
          const isActive = activeCohort === key;
          return (
            <button
              key={key}
              onClick={() => setActiveCohort(isActive ? 'all' : key)}
              className="shrink-0 text-left transition-all"
              style={{
                minWidth: 200, height: 120, background: '#ffffff', borderRadius: 12, padding: 16,
                border: isActive ? `1.5px solid ${config.color}` : '0.5px solid #e8e8e8',
                cursor: 'pointer',
              }}
              data-testid={`cohort-card-${key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon style={{ width: 14, height: 14, color: config.color }} />
                <span style={{ fontSize: 11, color: '#aaaaaa' }}>{data.patientCount} patients</span>
              </div>
              <p style={{ fontSize: 15, fontWeight: 500, color: '#111111', margin: '4px 0' }}>{config.label}</p>
              <p style={{ fontSize: 12, color: '#555555', marginBottom: 8 }}>{data.urgentGap}</p>
              <div className="flex items-center gap-2">
                <div style={{ flex: 1, height: 4, background: '#f0f0f0', borderRadius: 2 }}>
                  <div style={{ width: `${data.addressedRate}%`, height: '100%', background: config.color, borderRadius: 2, transition: 'width 300ms' }} />
                </div>
                <span style={{ fontSize: 10, color: '#aaaaaa', whiteSpace: 'nowrap' }}>{data.addressedRate}%</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Population Summary Insight ── */}
      <div
        style={{ background: '#f9fafb', borderRadius: '0 8px 8px 0', padding: '12px 16px', borderLeft: '3px solid #6b5ce7' }}
        data-testid="population-summary"
      >
        <div className="flex items-start gap-2.5">
          <Sparkles className="shrink-0 mt-0.5" style={{ width: 14, height: 14, color: '#6b5ce7' }} />
          <p style={{ fontSize: 13, color: '#555555', margin: 0, lineHeight: 1.6 }}>
            {isDemo
              ? 'Your diabetic panel: 8 of 42 patients are overdue for HbA1c testing. Highest priority: John Smith (9+ months overdue). Addressing this cohort could impact your MIPS Diabetes HbA1c quality measure score.'
              : 'Population insights will appear once your patient roster is connected.'}
          </p>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-2 flex-wrap">
          {FILTER_TABS.map(tab => {
            const count = tab.key === 'all' ? visibleAlerts.length : (COHORT_PATIENTS[tab.key]?.alerts.filter(a => ![...snoozed, ...resolved, ...dismissed].includes(a.id)).length || 0);
            return (
              <Button key={tab.key} size="sm" variant={activeCohort === tab.key ? 'default' : 'outline'}
                className={cn("text-xs", activeCohort === tab.key && "bg-black")}
                onClick={() => setActiveCohort(tab.key)}
                data-testid={`insights-tab-${tab.key}`}
              >
                {tab.label}
                {tab.key !== 'all' && <Badge className="ml-1.5 bg-slate-200 text-slate-600 text-[10px] px-1.5">{count}</Badge>}
              </Button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <Select value={patientFilter} onValueChange={setPatientFilter}>
            <SelectTrigger className="h-8 w-[140px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All patients</SelectItem>
              <SelectItem value="high_risk">High Risk</SelectItem>
              <SelectItem value="moderate">Moderate</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="urgency">Sort: Urgency</SelectItem>
              <SelectItem value="patient">Sort: Patient</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ── Cohort-Organized Alert Sections ── */}
      <div className="space-y-4" data-testid="insights-list">
        {Object.entries(groupedByCohort).map(([cohort, alerts]) => {
          const config = COHORT_CONFIG[cohort];
          if (!config) return null;
          const Icon = config.icon;
          const isExpanded = expandedSections[cohort];
          const displayAlerts = isExpanded ? alerts : alerts.slice(0, 3);

          return (
            <div key={cohort} data-testid={`cohort-section-${cohort}`}>
              {/* Section Header */}
              <button
                onClick={() => toggleSection(cohort)}
                className="w-full flex items-center gap-2.5 mb-2 group"
                data-testid={`cohort-toggle-${cohort}`}
              >
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: config.color, flexShrink: 0 }} />
                <span style={{ fontSize: 14, fontWeight: 500, color: '#111111' }}>{config.label}</span>
                <Badge className="bg-slate-100 text-slate-500 text-[10px] border-0">{alerts.length} patients</Badge>
                {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-slate-400 ml-auto" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400 ml-auto" />}
              </button>

              {/* Alert Rows */}
              <div className="space-y-2">
                {displayAlerts.map(alert => (
                  <div
                    key={alert.id}
                    className="flex items-start justify-between gap-4 p-3 rounded-lg bg-white border border-slate-100 hover:border-slate-200 transition-colors"
                    style={{ borderLeft: `3px solid ${config.color}` }}
                    data-testid={`insight-${alert.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={cn("text-[10px]", SEVERITY_BADGE[alert.severity])}>{alert.severity}</Badge>
                        <button
                          onClick={() => navigate(`/lumina/patients/${alert.patientId}`)}
                          className="text-xs font-semibold text-violet-600 hover:text-violet-800 transition-colors"
                        >
                          {alert.patient}
                        </button>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">{alert.desc}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" className="bg-black hover:bg-gray-800 gap-1 text-xs h-7" onClick={() => handleAction(alert)} data-testid={`insight-action-${alert.id}`}>
                        {alert.action} <ArrowRight className="h-3 w-3" />
                      </Button>
                      {/* High severity: overflow menu instead of dismiss */}
                      {alert.severity === 'high' ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0"><MoreHorizontal className="h-3.5 w-3.5 text-slate-400" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => { setSnoozed(p => [...p, alert.id]); toast.info('Snoozed for 7 days'); }}>
                              <ClockIcon className="h-3.5 w-3.5 mr-2" /> Snooze 7 days
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setResolved(p => [...p, alert.id]); toast.success('Marked as resolved'); }}>
                              <CheckCircle2 className="h-3.5 w-3.5 mr-2" /> Mark resolved with note
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <Button size="sm" variant="ghost" className="text-xs text-muted-foreground h-7" onClick={() => { setDismissed(p => [...p, alert.id]); toast.success('Dismissed'); }}>
                          Dismiss
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {!isExpanded && alerts.length > 3 && (
                  <button onClick={() => toggleSection(cohort)} className="text-xs text-violet-600 hover:text-violet-800 font-medium pl-3 py-1" data-testid={`expand-${cohort}`}>
                    Show {alerts.length - 3} more &rarr;
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {Object.keys(groupedByCohort).length === 0 && (
          <Card><CardContent className="p-8 text-center text-muted-foreground"><CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-violet-300" /><p className="text-sm">No active care gaps in this category</p></CardContent></Card>
        )}
      </div>
    </div>
  );
};

export default LuminaAIInsights;
